import { useTranslation } from "react-i18next";
import { useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEOMeta from "@/components/base/SEOMeta";
import SchemaOrg from "@/components/base/SchemaOrg";

export default function About() {
  const { t } = useTranslation();

  const milestones = [
    { year: "2024.03", event: "好坏动漫正式上线，发布首批12篇深度评测" },
    { year: "2024.06", event: "单月访问量突破10万，评测作品扩展至30+部" },
    { year: "2024.09", event: "推出「好坏排行榜」，成为动漫爱好者筛选工具" },
    { year: "2024.12", event: "月访问量突破50万，建立8大分类评测体系" },
  ];

  const team = [
    {
      name: "动漫老司机",
      role: "首席评测师",
      desc: "拥有15年动漫观看经验，阅片量超过2000部，擅长剧情分析与世界观解读。",
      image:
        "https://readdy.ai/api/search-image?query=Anime%20style%20portrait%20of%20a%20male%20anime%20critic%20with%20glasses%2C%20warm%20coral%20and%20beige%20background%2C%20professional%20yet%20friendly%2C%20minimalist%20illustration%20style%2C%20soft%20lighting&width=400&height=400&seq=team-1&orientation=squarish",
    },
    {
      name: "二次元观察员",
      role: "资深编辑",
      desc: "前动漫杂志编辑，擅长发掘小众佳作，对动画制作流程与行业动态有深入了解。",
      image:
        "https://readdy.ai/api/search-image?query=Anime%20style%20portrait%20of%20a%20female%20anime%20editor%2C%20warm%20amber%20and%20cream%20background%2C%20creative%20and%20approachable%2C%20minimalist%20illustration%20style%2C%20soft%20lighting&width=400&height=400&seq=team-2&orientation=squarish",
    },
    {
      name: "作画研究所",
      role: "视觉分析师",
      desc: "动画专业出身，专注于动画作画质量、分镜设计与美术风格的深度分析。",
      image:
        "https://readdy.ai/api/search-image?query=Anime%20style%20portrait%20of%20a%20male%20animator%20with%20digital%20tablet%2C%20warm%20taupe%20and%20beige%20background%2C%20artistic%20and%20focused%2C%20minimalist%20illustration%20style%2C%20soft%20lighting&width=400&height=400&seq=team-3&orientation=squarish",
    },
  ];

  const faqs = [
    {
      q: "好坏动漫的评测标准是什么？",
      a: "我们从剧情、作画、配乐、角色塑造和整体观感五个维度进行综合打分，满分10分。每个评测都包含「好」与「坏」两方面的客观分析，帮助读者全面了解作品的优劣。",
    },
    {
      q: "评测文章多久更新一次？",
      a: "我们保持每周至少发布2-3篇新评测。在每季新番开播后的第3-4周，会集中发布当季新番的初评；完结后会发布完整的深度评测。",
    },
    {
      q: "可以投稿评测吗？",
      a: "当然可以！我们欢迎动漫爱好者投稿。请将您的评测稿件发送至 contact@haohuai.me，我们的编辑团队会在3个工作日内回复。投稿被采纳的作者将获得署名和相应稿酬。",
    },
    {
      q: "好坏动漫的评分是否客观？",
      a: "我们尽力保持客观，但也承认每个人的审美和偏好不同。因此我们会在每篇评测中明确列出优缺点，让读者可以根据自己的偏好来判断。我们建议读者参考多篇评测，而非仅依赖单一分数。",
    },
    {
      q: "为什么有些热门动漫评分不高？",
      a: "热门不等于高品质。我们不以人气为标准打分，而是基于作品的制作质量、叙事深度和创新性来评判。有些作品人气很高但制作粗糙，我们会如实指出问题。这正是「好坏动漫」的使命——帮助大家避开踩雷。",
    },
  ];

  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem("phone_alt") as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus("success");
      return;
    }

    try {
      const formData = new FormData(form);
      const submitAddr = "https://readdy.ai/api/form/d9qrbtsnlsngrm4lm4kg";
      const response = await fetch(submitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setFormError("");
        form.reset();
      } else {
        const msg =
          parsed?.meta?.message || parsed?.message || responseText || "提交失败，请稍后重试";
        if (typeof msg === "string" && (msg.includes("spam") || msg.includes("form data is spam"))) {
          setFormStatus("error");
          setFormError("提交失败，请稍后重试");
        } else {
          setFormStatus("error");
          setFormError(msg);
        }
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请检查连接后重试");
    }
  };

  return (
    <>
      <SEOMeta
        title={t("seo.aboutTitle")}
        description={t("seo.aboutDesc")}
        keywords="好坏动漫,关于我们,动漫评测平台,动漫推荐,联系我们"
        canonicalUrl="https://haohuai.me/about"
      />
      <SchemaOrg
        type="AboutPage"
        data={{
          name: t("seo.aboutTitle"),
          url: "https://haohuai.me/about",
          description: t("seo.aboutDesc"),
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.a,
            },
          })),
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://haohuai.me" },
            { "@type": "ListItem", position: 2, name: "关于我们" },
          ],
        }}
      />

      <main className="min-h-screen pt-16">
        <div className="container-site py-8">
          <Breadcrumb items={[{ label: t("about.title") }]} />

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mt-4 mb-8">
            {t("about.title")}
          </h1>

          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="text-xl font-semibold text-foreground-800 font-heading mb-4">
                  {t("about.intro")}
                </h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  {t("about.introText")}
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {t("about.missionText")}
                </p>
              </div>
              <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20with%20anime%20posters%20on%20wall%2C%20warm%20coral%20and%20gold%20accents%2C%20cozy%20atmosphere%2C%20minimalist%20design%2C%20Japanese%20inspired%20decor%2C%20soft%20natural%20lighting&width=800&height=600&seq=about-hero&orientation=landscape"
                  alt="好坏动漫工作室"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="mb-16 py-12 bg-background-100 rounded-lg -mx-4 px-4 md:mx-0 md:px-8">
            <h2 className="text-xl font-semibold text-foreground-800 font-heading mb-8 text-center">
              发展历程
            </h2>
            <div className="max-w-2xl mx-auto">
              {milestones.map((m, idx) => (
                <div key={idx} className="flex gap-4 mb-6 last:mb-0">
                  <div className="flex-shrink-0 w-20 text-right">
                    <span className="text-sm font-semibold text-primary-600 font-heading">
                      {m.year}
                    </span>
                  </div>
                  <div className="relative flex-shrink-0 flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-primary-500"></div>
                    {idx < milestones.length - 1 && (
                      <div className="w-0.5 flex-1 bg-primary-200 mt-1"></div>
                    )}
                  </div>
                  <div className="pb-2">
                    <p className="text-sm text-foreground-700">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl font-semibold text-foreground-800 font-heading mb-8 text-center">
              核心团队
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {team.map((member) => (
                <div
                  key={member.name}
                  className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-base font-semibold text-foreground-800 font-heading">
                    {member.name}
                  </h3>
                  <p className="text-xs text-primary-600 font-medium mt-1">{member.role}</p>
                  <p className="text-xs text-foreground-600 mt-3 leading-relaxed">{member.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16 py-12 bg-background-100 rounded-lg -mx-4 px-4 md:mx-0 md:px-8">
            <h2 className="text-xl font-semibold text-foreground-800 font-heading mb-8 text-center">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, idx) => (
                <details
                  key={idx}
                  className="group bg-background-50 rounded-lg border border-background-200/70 cursor-pointer"
                >
                  <summary className="flex items-center justify-between p-4 text-sm font-medium text-foreground-800 font-heading">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground-800 font-heading mb-2 text-center">
              {t("about.contact")}
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-8">{t("about.contactDesc")}</p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">{t("about.email")}</p>
                    <p className="text-sm font-medium text-foreground-800">
                      {t("about.emailAddr")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-wechat-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">{t("about.wechat")}</p>
                    <p className="text-sm font-medium text-foreground-800">{t("about.wechatId")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                    <i className="ri-bilibili-line text-secondary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">{t("about.bilibili")}</p>
                    <p className="text-sm font-medium text-foreground-800">
                      {t("about.bilibiliId")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                <h3 className="text-sm font-semibold text-foreground-800 font-heading mb-4">
                  发送消息
                </h3>
                {formStatus === "success" ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-check-line text-primary-600 text-xl"></i>
                    </div>
                    <p className="text-sm text-foreground-700">消息已发送！我们会尽快回复。</p>
                    <button
                      onClick={() => setFormStatus("idle")}
                      className="mt-4 text-xs text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
                    >
                      发送新消息
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form="true">
                    <div className="hp-field-wrap">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    <div className="space-y-4">
                      <div>
                        <input
                          type="text"
                          name="name"
                          placeholder="你的名字"
                          required
                          className="w-full px-4 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          placeholder="你的邮箱"
                          required
                          className="w-full px-4 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          placeholder="你想说的话..."
                          rows={4}
                          maxLength={500}
                          required
                          className="w-full px-4 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                      </div>
                      {formStatus === "error" && (
                        <p className="text-xs text-primary-600">{formError}</p>
                      )}
                      <button
                        type="submit"
                        className="w-full py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                      >
                        发送消息
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}